import { useMemo } from 'react';
import OnelivePointList, { type PointListRow } from './OnelivePointList';
import { oneliveAssets } from './assets';
import {
  formatAmount,
  type ExchangeItem,
  type ItemShopCharge,
  type ItemShopOrder,
  type ItemShopWallet,
} from './api';
import type { MypageProfileTab } from './mypageMenu';
import { useItemShopWalletData } from './useItemShopWalletData';

const EXCHANGE_STATUS_LABEL: Record<string, string> = {
  pending: '처리대기',
  completed: '처리완료',
  cancelled: '부결처리',
};

const SECTION_TITLES: Record<MypageProfileTab, string> = {
  coin: '코인내역',
  charge: '충전내역',
  exchange: '환전내역',
};

interface Props {
  variant: MypageProfileTab;
}

function orderStatusLabel(order: ItemShopOrder): string {
  return order.status_label
    || (order.status === 'completed' ? '처리완료' : order.status === 'rejected' ? '부결처리' : '처리대기');
}

function chargeStatusLabel(charge: ItemShopCharge): string {
  return charge.status_label
    || (charge.status === 'completed' ? '처리완료' : charge.status === 'rejected' ? '부결처리' : '처리대기');
}

function buildChargeRows(wallet: ItemShopWallet | null): PointListRow[] {
  const bankOrders = (wallet?.orders ?? []).filter(
    (order) => (order.payment_method ?? 'bank') !== 'coin',
  );

  if (bankOrders.length > 0) {
    return bankOrders
      .map((order, index) => {
        const isCoinCharge = order.item_name === '코인';
        const label = isCoinCharge ? '코인 충전' : String(order.item_name || '상품 주문');
        return {
          id: `order-${order.id ?? index}`,
          title: order.id ? `[#${order.id}] ${label}` : label,
          amountText: formatAmount(order.price),
          date: order.ordered_at,
          statusLabel: orderStatusLabel(order),
        };
      })
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }

  return (wallet?.charges ?? [])
    .map((charge, index) => ({
      id: `charge-${index}`,
      title: charge.note || '코인 충전',
      amountText: formatAmount(charge.amount),
      date: charge.charged_at,
      statusLabel: chargeStatusLabel(charge),
    }))
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
}

function buildCoinRows(wallet: ItemShopWallet | null, exchanges: ExchangeItem[]): PointListRow[] {
  const rows: PointListRow[] = [];

  (wallet?.charges ?? []).forEach((charge, index) => {
    rows.push({
      id: `charge-${index}`,
      title: charge.note || '코인 충전',
      amountText: `+${formatAmount(charge.amount)}`,
      date: charge.charged_at,
      statusLabel: chargeStatusLabel(charge),
    });
  });

  (wallet?.orders ?? []).forEach((order, index) => {
    rows.push({
      id: `order-${index}`,
      title: `[${order.item_name}] 주문신청 ${formatAmount(order.price)}`,
      amountText: `+${formatAmount(order.coin)}`,
      date: order.ordered_at,
      statusLabel: orderStatusLabel(order),
    });
  });

  exchanges.forEach((item) => {
    rows.push({
      id: `exchange-${item.id}`,
      title: item.content || '코인환전',
      amountText: `-${formatAmount(item.amount)}`,
      date: item.created_at,
      isMinus: true,
    });
  });

  return rows.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
}

function buildExchangeRows(exchanges: ExchangeItem[]): PointListRow[] {
  return exchanges.map((item) => ({
    id: `exchange-${item.id}`,
    title: [item.account_holder, item.bank_name, item.account_number].filter(Boolean).join(' ') || item.content || '환전신청',
    amountText: formatAmount(item.amount),
    date: item.created_at,
    statusLabel: item.status_label || EXCHANGE_STATUS_LABEL[item.status || ''] || item.status || '접수',
    isMinus: true,
  }));
}

export default function OneliveMypageHistoryContent({ variant }: Props) {
  const { loading, wallet, exchanges } = useItemShopWalletData(50);
  const title = SECTION_TITLES[variant];

  const rows = useMemo(() => {
    if (variant === 'coin') return buildCoinRows(wallet, exchanges);
    if (variant === 'charge') return buildChargeRows(wallet);
    return buildExchangeRows(exchanges);
  }, [variant, wallet, exchanges]);

  const emptyText = variant === 'charge'
    ? '충전 내역이 없습니다.'
    : variant === 'coin'
      ? '코인 내역이 없습니다.'
      : '환전 내역이 없습니다.';

  return (
    <div className="onelive-mypage-history-body">
      <div className="onelive-mypage-history-head">
        <span className="onelive-mypage-history-head-icon" aria-hidden="true">
          <img src={oneliveAssets.drawerCoin} alt="" />
        </span>
        <h2 className="onelive-mypage-history-head-title">{title}</h2>
      </div>
      <div className="onelive-mypage-history-panel">
        {loading ? (
          <p className="onelive-loading">로딩 중...</p>
        ) : (
          <OnelivePointList items={rows} emptyText={emptyText} />
        )}
      </div>
    </div>
  );
}
